import Vue from 'vue';
import Router from 'vue-router';
import { appLayouts } from '@/enums';
import Vacancy from './orm/models/Vacancy/Vacancy';
import User from './orm/models/User/User';
import { extractUtm } from './functions';

Vue.use(Router);

const guardIfNotAuthenticated = (to, from, next) => {
  if (User.getAuthUser() === null) {
    const query = new URLSearchParams(to.query);
    return next({ name: 'login', query: { redirect: `${to.path}?${query.toString()}` } });
  }
  return next();
};

const guardIfAuthenticated = (to, from, next) => {
  if (User.getAuthUser() !== null) {
    return next({ name: 'dashboard' });
  }
  return next();
};

export const routes = [
  {
    path: '/',
    name: 'dashboard',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "dashboard" */ './views/Dashboard.vue'),
    props: () => ({
      company: User.getAuthUser().company,
      user: User.getAuthUser(),
    }),
  },
  {
    path: '/test',
    name: 'inviteCandidate',
    component: () => import(/* webpackChunkName: "vacancy-candidates" */ './views/CandidateInvitation.vue'),
  },
  {
    path: '/integrations',
    name: 'integrations',
    component: () => import(/* webpackChunkName: "integrations" */ './views/Integrations'),
    meta: {
      layout: appLayouts.INTEGRATIONS,
    },
  },
  {
    path: '/rabota-ru/login',
    name: 'rabota-login',
    component: () => import(/* webpackChunkName: "rabota-login" */ './views/RabotaRuAuth.vue'),
  },
  {
    path: '/login',
    name: 'login',
    beforeEnter: guardIfAuthenticated,
    component: () => import(/* webpackChunkName: "login" */ './views/Login.vue'),
    meta: { showMenus: false },
  },
  {
    path: '/change-temp-password/:uid',
    name: 'change-temp-password',
    component: () => import(/* webpackChunkName: "login" */ './views/ChangeTempPasswordByUniqueLink.vue'),
    props: (route) => ({
      uid: route.params.uid,
    }),
    meta: {
      layout: appLayouts.INVITE,
    },
  },
  {
    path: '/logout',
    name: 'logout',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "logout" */ './views/Logout.vue'),
  },
  {
    path: '/registration',
    name: 'registration',
    beforeEnter: guardIfAuthenticated,
    component: () => import(/* webpackChunkName: "registration" */ './views/Registration.vue'),
  },
  {
    path: '/payment',
    name: 'payment',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "payment" */ './views/Payment.vue'),
  },
  {
    path: '/qr/add',
    name: 'qr-add',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "qr-add" */ './views/VacanciesListDetailAddPage.vue'),
  },
  {
    path: '/qr/:id',
    name: 'qr-detail',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "qr-detail" */ './views/VacanciesListDetailPage.vue'),
    props: (route) => ({
      id: Number.parseInt(route.params.id),
    }),
  },
  {
    path: '/qr',
    name: 'qr-list',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "qr-list" */ './views/VacancyLists.vue'),
  },
  {
    path: '/candidates',
    name: 'candidates-list',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "qr-list" */ './views/Candidates.vue'),
  },
  {
    path: '/partnership',
    name: 'partnership',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "partnership" */ './views/Partnership.vue'),
  },
  {
    path: '/settings/:type?/:addType?',
    name: 'settings',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "settings" */ './views/Settings.vue'),
    props: (route) => ({
      user: User.getAuthUser(),
      type: route.params.type,
      addType: route.params.addType,
      planId: route.params.planId,
      planTitle: route.params.planTitle,
      periodId: route.params.periodId,
    }),
  },
  {
    path: '/notifications',
    name: 'notifications',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "notifications" */ './views/Notifications.vue'),
  },
  {
    path: '/order/:type/:id',
    name: 'order',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "order" */ './views/Order.vue'),
    props: (route) => ({
      id: route.params.id,
      type: route.params.type,
    }),
  },
  {
    path: '/order/:type/:id/success/:operation',
    name: 'order-success',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "order-success" */ './views/Order/OrderSuccess.vue'),
    props: (route) => ({
      id: Number.parseInt(route.params.id, 10),
      type: route.params.type,
      operation: route.params.operation,
    }),
  },
  // TODO: Убрать коменты когда покупка квот будет готова к тестам #ADD_QUOTA
  // {
  //   path: '/order/quota/success/:type',
  //   name: 'quota-order-success',
  //   beforeEnter: guardIfNotAuthenticated,
  //   component: () => import(/* webpackChunkName: "order-success" */ './views/Order/OrderQuotaSuccess.vue'),
  //   props: (route) => ({
  //     type: route.params.type,
  //   }),
  // },
  {
    path: '/order/failed',
    name: 'order-failed',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "order-success" */ './views/Order/OrderFailed.vue'),
  },
  {
    path: '/oauth/authorize',
    name: 'oauth-authorization',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "login" */ './views/Oauth/Authorization.vue'),
    props: (route) => ({ clientId: Number.parseInt(route.query.client_id, 10) }),
    meta: { showMenus: false },
  },
  {
    path: '/password-recovery',
    name: 'password-recovery',
    beforeEnter: guardIfAuthenticated,
    component: () => import(/* webpackChunkName: "recovery-password" */ './views/ForgotPassword.vue'),
  },
  {
    path: '/password-reset',
    name: 'password-reset',
    beforeEnter: guardIfAuthenticated,
    component: () => import(/* webpackChunkName: "reset-password" */ './views/ResetPassword.vue'),
  },
  {
    path: '/verify-email',
    name: 'verifyEmail',
    component: () => import(/* webpackChunkName: "verify-email" */ './views/VerifyEmail.vue'),
    props: () => ({ user: User.getAuthUser() }),
  },
  {
    path: '/vacancies/:status?',
    name: 'vacancies',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "vacancies" */ './views/Vacancies.vue'),
    props: (route) => ({
      company: User.getAuthUser().company,
      status: route.params.status,
    }),
  },
  {
    path: '/hh/vacancies/import',
    name: 'hh-vacancies-import',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "hh-vacancies-import" */ './views/HHVacanciesImport.vue'),
  },
  {
    path: '/hh/authorization/error',
    name: 'hh-authorization-error',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "hh-authorization-error" */ './views/HHAuthorizationError.vue'),
  },
  {
    path: '/company/about',
    name: 'company',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "company" */ './views/Company.vue'),
    props: (route) => ({
      company: User.getAuthUser().company,
      type: route.params.type,
    }),
  },
  {
    path: '/career-portal/:type?/:addType?',
    name: 'career-portal',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "career-portal" */ './views/CareerPortal.vue'),
    props: (route) => ({
      company: User.getAuthUser().company,
      type: route.params.type,
      addType: route.params.addType,
    }),
  },
  {
    path: '/articles/create',
    name: 'article-create',
    component: () => import(/* webpackChunkName: "article-create" */ './views/Article/ArticleCreate.vue'),
  },
  {
    path: '/articles/:id',
    name: 'article-edit',
    component: () => import(/* webpackChunkName: "article-edit" */ './views/Article/ArticleEdit.vue'),
  },
  {
    path: '/vacancy-new/:id?',
    name: 'vacancy-store',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "vacancy-step-store" */ './views/Vacancy/VacancyStepStore.vue'),
    props: (route) => ({
      user: User.getAuthUser(),
      company: User.getAuthUser().company,
      vacancyId: Number.parseInt(route.params.id, 10),
    }),
  },
  {
    path: '/vacancy/:vacancyId/preset',
    name: 'vacancy-preset',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "vacancy-step-preset" */ './views/Vacancy/VacancyStepPreset.vue'),
    props: (route) => ({ vacancyId: Number.parseInt(route.params.vacancyId, 10) }),
  },
  {
    path: '/vacancy/:vacancyId/wizard/common/interviewer/:interviewerId/index/:index',
    name: 'vacancy-wizard',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(
      /* webpackChunkName: "vacancy-step-wizard" */ './views/Vacancy/VacancyStepWizardCommon.vue'
    ),
    props: (route) => ({
      vacancyId: Number.parseInt(route.params.vacancyId, 10),
      interviewerId: Number.parseInt(route.params.interviewerId, 10),
      index: Number.parseInt(route.params.index, 10),
    }),
  },
  {
    path:
      '/vacancy/:vacancyId/wizard/common/interviewer/:interviewerId/index/:index/category/:categoryId',
    name: 'vacancy-wizard-category',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(
      /* webpackChunkName: "vacancy-step-wizard" */ './views/Vacancy/VacancyStepWizardCommon.vue'
    ),
    props: (route) => ({
      vacancyId: Number.parseInt(route.params.vacancyId, 10),
      interviewerId: Number.parseInt(route.params.interviewerId, 10),
      index: Number.parseInt(route.params.index, 10),
      categoryId: Number.parseInt(route.params.categoryId, 10),
    }),
  },
  {
    path: '/vacancy/:vacancyId/wizard/special/interviewer/:interviewerId',
    name: 'vacancy-wizard-special',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(
      /* webpackChunkName: "vacancy-step-wizard" */ './views/Vacancy/VacancyStepWizardSpecial.vue'
    ),
    props: (route) => ({
      vacancyId: Number.parseInt(route.params.vacancyId, 10),
      interviewerId: Number.parseInt(route.params.interviewerId, 10),
    }),
  },
  {
    path: '/vacancy/:vacancyId/questions/interviewer/:interviewerId',
    name: 'vacancy-questions',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(
      /* webpackChunkName: "vacancy-step-questions" */ './views/Vacancy/VacancyStepQuestions.vue'
    ),
    props: (route) => ({
      vacancyId: Number.parseInt(route.params.vacancyId, 10),
      interviewerId: Number.parseInt(route.params.interviewerId, 10),
    }),
  },
  {
    path: '/vacancy/:vacancyId/questions/avatar',
    name: 'vacancy-ai-questions',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(
      /* webpackChunkName: "vacancy-step-questions" */ './views/Vacancy/VacancyStepAiQuestions.vue'
    ),
    props: (route) => ({
      vacancyId: Number.parseInt(route.params.vacancyId, 10),
    }),
  },
  {
    path: '/vacancy/:vacancyId/questions/avatar/edit',
    name: 'vacancy-ai-questions-edit',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(
      /* webpackChunkName: "vacancy-step-questions" */ './views/Vacancy/VacancyAiQuestionsEdit.vue'
    ),
    props: (route) => ({
      vacancyId: Number.parseInt(route.params.vacancyId, 10),
      redactingAi: true,
    }),
  },
  {
    path: '/vacancy/:vacancyId/faq',
    name: 'vacancy-faq',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "vacancy-step-faq" */ './views/Vacancy/VacancyStepFaq.vue'),
    props: (route) => ({ vacancyId: Number.parseInt(route.params.vacancyId, 10) }),
  },
  {
    path: '/vacancy/:vacancyId/publish',
    name: 'vacancy-publish',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "vacancy-step-publish" */ './views/Vacancy/VacancyStepPublish.vue'),
    props: (route) => ({ vacancyId: Number.parseInt(route.params.vacancyId, 10) }),
  },
  {
    path: '/vacancy/:vacancyId/test',
    name: 'vacancy-testing',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(
      /* webpackChunkName: "vacancy-step-testing" */ './views/Vacancy/VacancyStepTest.vue'
    ),
    props: (route) => ({
      vacancyId: Number.parseInt(route.params.vacancyId, 10),
    }),
  },
  {
    path: '/vacancy/:vacancyId/test/edit',
    name: 'vacancy-testing-edit',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(
      /* webpackChunkName: "vacancy-step-testing-edit" */ './views/Vacancy/VacancyTest.vue'
    ),
    props: (route) => ({
      vacancyId: Number.parseInt(route.params.vacancyId, 10),
    }),
  },
  {
    path: '/vacancy/:vacancyId/invite-method',
    name: 'vacancy-invite-method',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(
      /* webpackChunkName: "vacancy-step-faq" */ './views/Vacancy/VacancyStepIntegration.vue'
    ),
    props: (route) => ({ vacancyId: Number.parseInt(route.params.vacancyId, 10) }),
  },
  {
    path: '/vacancy/:id',
    beforeEnter: guardIfNotAuthenticated,
    component: () => import(/* webpackChunkName: "vacancy-home" */ './views/VacancyHome.vue'),
    props: (route) => ({
      company: User.getAuthUser().company,
      vacancyId: Number.parseInt(route.params.id, 10),
    }),
    children: [
      {
        path: '',
        component: () => import(/* webpackChunkName: "vacancy" */ './components/Vacancy/Vacancy.vue'),
        children: [
          {
            path: '',
            name: 'vacancy',
            component: () => import(/* webpackChunkName: "vacancy-card" */ './components/Vacancy/VacancyCard.vue'),
            props: (route) => ({
              vacancy: Vacancy.query()
                .withAllRecursive()
                .find(route.params.id),
            }),
          },
          {
            path: 'edit',
            name: 'vacancy-edit',
            component: () => import(/* webpackChunkName: "vacancy-edit" */ './components/Vacancy/VacancyEdit.vue'),
            props: (route) => ({
              user: User.getAuthUser(),
              company: User.getAuthUser().company,
              vacancy: Vacancy.query()
                .withAllRecursive()
                .find(route.params.id),
            }),
          },
          {
            path: 'questions/edit',
            name: 'vacancy-questions-edit',
            component: () => import(
              /* webpackChunkName: "vacancy-questions-edit" */ './components/Vacancy/VacancyQuestionsEdit.vue'
            ),
          },
          {
            path: 'faq/edit',
            name: 'vacancy-faq-edit',
            component: () => import(
              /* webpackChunkName: "vacancy-faq-edit" */ './components/Vacancy/VacancyFaqEdit.vue'
            ),
            props: (route) => ({
              vacancy: Vacancy.query()
                .withAllRecursive()
                .find(route.params.id),
            }),
          },
          {
            path: 'ai-processor-settings',
            name: 'vacancy-ai-processor-settings',
            component: () => import(
              /* webpackChunkName: "vacancy-ai-processor-settings" */ './views/Vacancy/VacancyAiProcessorSettings.vue'
            ),
            props: (route) => ({
              vacancyId: Number.parseInt(route.params.id, 10),
            }),
          },
        ],
      },
      {
        path: 'candidates',
        name: 'candidates',
        component: () => import(
          /* webpackChunkName: "vacancy-candidates" */ './components/Vacancy/VacancyCandidates.vue'
        ),
        props: (route) => ({
          vacancy: Vacancy.query()
            .withAllRecursive()
            .find(route.params.id),
        }),
        children: [
          {
            path: ':candidateId',
            name: 'candidate',
            component: () => import(/* webpackChunkName: "candidate" */ './views/Candidate.vue'),
            props: (route) => ({
              vacancy: Vacancy.query().find(route.params.id),
              candidateId: route.params.candidateId,
            }),
          },
        ],
      },
    ],
  },
  {
    path: '/instructions-domain',
    name: 'instructions-domain',
    component: () => import(/* webpackChunkName: "instructions-domain" */ './views/InstructionsDomain'),
  },
  {
    path: '/privacy-policy',
    name: 'privacy-policy',
    component: () => import(/* webpackChunkName: "privacy-policy" */ './views/PrivacyPolicy'),
  },
  {
    path: '*',
    component: () => import(/* webpackChunkName: "error-404" */ './views/Error404.vue'),
  },
];

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior: (to, from, savedPosition) => {
    if (from.name === 'candidates' && to.name === 'candidate') {
      return null;
    }

    if (from.name === 'candidate' && to.name === 'candidates') {
      return null;
    }

    if (savedPosition) {
      return savedPosition;
    }

    return savedPosition || { x: 0, y: 0 };
  },
  routes,
});

const utmGuard = router.beforeEach((to, from, next) => {
  extractUtm(to.query);
  utmGuard();
  const user = User.getAuthUser();

  if (user?.createdUid) {
    next({ name: 'logout' });
  } else {
    next();
  }
});

export default router;
